import type { NightSchedule, WorkScheduleSchedule } from "@innoclouds/api";
import type { Dayjs } from "dayjs";
import { parseTimeToMinutes } from "./datetime";

type TimeWindow = { from: number; to: number };

export const getDayOfWeek = (date: Dayjs) => {
	const day = date.day();
	return day === 0 ? 7 : day;
};

export const getMinutesOfDay = (date: Dayjs) => date.hour() * 60 + date.minute();

const getWindow = (open?: string | null, close?: string | null): TimeWindow | null => {
	const from = parseTimeToMinutes(open ?? undefined);
	const to = parseTimeToMinutes(close ?? undefined);
	if (from === null || to === null || from === to) return null;
	return { from, to };
};

const getNightWindows = (night?: NightSchedule | null) => ({
	am: getWindow(night?.am_night_open_time, night?.am_night_close_time),
	pm: getWindow(night?.pm_night_open_time, night?.pm_night_close_time),
});

const isWindowOpenToday = (window: TimeWindow | null, currentMinutes: number) => {
	if (!window) return false;
	if (window.from < window.to) return currentMinutes >= window.from && currentMinutes < window.to;
	return currentMinutes >= window.from;
};

const isWindowOpenSinceYesterday = (window: TimeWindow | null, currentMinutes: number) => !!window && window.from > window.to && currentMinutes < window.to;

export const isInNightWindow = (night: NightSchedule | null | undefined, currentMinutes: number) => {
	const { am, pm } = getNightWindows(night);
	return [am, pm].some((window) => isWindowOpenToday(window, currentMinutes) || isWindowOpenSinceYesterday(window, currentMinutes));
};

export const isInYesterdayNightOverflow = (night: NightSchedule | null | undefined, currentMinutes: number) =>
	isWindowOpenSinceYesterday(getNightWindows(night).pm, currentMinutes);

export const isNightAt = (schedule: WorkScheduleSchedule[] | null | undefined, at: Dayjs) => {
	if (!schedule?.length) return false;

	const findDay = (day: number) => schedule.find((item) => item.day_of_week === day);
	const today = findDay(getDayOfWeek(at));
	const yesterday = findDay(getDayOfWeek(at.subtract(1, "day")));
	const currentMinutes = getMinutesOfDay(at);

	return (
		(!!today && !today.is_weekend && isInNightWindow(today.night, currentMinutes)) ||
		(!!yesterday && !yesterday.is_weekend && isInYesterdayNightOverflow(yesterday.night, currentMinutes))
	);
};

export const isScheduleVisibleAt = (schedule: WorkScheduleSchedule[] | null | undefined, at: Dayjs) => {
	if (!schedule?.length) return true;

	const findDay = (day: number) => schedule.find((item) => item.day_of_week === day);
	const today = findDay(getDayOfWeek(at));
	const yesterday = findDay(getDayOfWeek(at.subtract(1, "day")));
	const currentMinutes = getMinutesOfDay(at);

	if (today && !today.is_weekend && isWindowOpenToday(getWindow(today.open_time, today.close_time), currentMinutes)) return true;
	if (yesterday && !yesterday.is_weekend && isWindowOpenSinceYesterday(getWindow(yesterday.open_time, yesterday.close_time), currentMinutes)) return true;

	return isNightAt(schedule, at);
};
